import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { CA_DATA } from "../../models/data.model";
import { exportCaCumuleExcel } from "./ca-cumule-excel.utils";

/** Échelle d'agrégation. Le widget est une courbe cumulée, mais dans le dialog
 *  on peut zoomer sur une granularité différente pour analyser la progression. */
export type CaCumuleScale = "j" | "s" | "m" | "a";

export interface CaCumuleDialogData {
  /** Données CA brutes déjà filtrées par timeScale côté widget. */
  rawData: CA_DATA["data"];
  /** Échelle initiale à l'ouverture (par défaut : mois). */
  initialPeriod: CaCumuleScale;
  /** Libellé humain du timeScale courant (ex: "Depuis le début de l'année"). */
  timeScaleLabel: string;
}

/** Palette de couleurs cyclées sur les lignes (cohérente avec les widgets
 *  répartition). */
const ROW_PALETTE = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
  "#8cd17d", "#a0cbe8",
];

interface ClientEntry {
  date: string;
  dateLabel: string;
  montant: number;
  factureIds: string[];
  commandeIds: string[];
}

interface ClientRow {
  denomination: string;
  ca: number;
  partPeriod: number;
  nbEntries: number;
  entries: ClientEntry[];
}

interface PeriodRow {
  key: string;
  label: string;
  anchor: Date;
  ca: number;
  cumul: number;
  variationPct: number | null;
  partPct: number;
  nbClients: number;
  clients: ClientRow[];
  color: string;
}

@Component({
  selector: "vl-ca-cumule-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    DialogHeaderExportComponent,
    ChipsToggleComponent,
    RadioCardComponent,
    DecimalPipe,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: "./ca-cumule-dialog.component.html",
  styleUrl: "./ca-cumule-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaCumuleDialogComponent {
  data = inject<CaCumuleDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CaCumuleDialogComponent>);

  exporting = signal(false);

  // ─── Granularité ────────────────────────────────────────────────────────
  scale = signal<CaCumuleScale>(this.data.initialPeriod);
  scaleElements = [
    { name: "Jour", value: "j" as CaCumuleScale },
    { name: "Semaine", value: "s" as CaCumuleScale },
    { name: "Mois", value: "m" as CaCumuleScale },
    { name: "Année", value: "a" as CaCumuleScale },
  ];
  scaleLabel: Record<CaCumuleScale, string> = {
    j: "Jour", s: "Semaine", m: "Mois", a: "Année",
  };
  scaleLabelSingularLower: Record<CaCumuleScale, string> = {
    j: "jour", s: "semaine", m: "mois", a: "année",
  };
  scaleLabelPlural: Record<CaCumuleScale, string> = {
    j: "jours", s: "semaines", m: "mois", a: "années",
  };
  scaleBestLabel: Record<CaCumuleScale, string> = {
    j: "Meilleur jour",
    s: "Meilleure semaine",
    m: "Meilleur mois",
    a: "Meilleure année",
  };

  // ─── Tri tableau ────────────────────────────────────────────────────────
  /** Par défaut on trie chronologiquement ASC pour rendre visible la
   *  progression du cumul (un cumul n'a pas de sens en DESC). */
  sortKey = signal<"date" | "ca" | "cumul">("date");
  sortDesc = signal<boolean>(false);

  toggleSort(key: "date" | "ca" | "cumul"): void {
    if (this.sortKey() === key) {
      this.sortDesc.set(!this.sortDesc());
    } else {
      this.sortKey.set(key);
      // date ASC par défaut (cumul progresse), CA/cumul DESC par défaut
      this.sortDesc.set(key !== "date");
    }
  }

  expandedPeriods = signal<Set<string>>(new Set());
  expandedClients = signal<Set<string>>(new Set());

  togglePeriod(key: string): void {
    const cur = new Set(this.expandedPeriods());
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    this.expandedPeriods.set(cur);
  }

  isExpanded(key: string): boolean {
    return this.expandedPeriods().has(key);
  }

  clientKey(periodKey: string, denomination: string): string {
    return `${periodKey}::${denomination}`;
  }

  toggleClient(periodKey: string, denomination: string): void {
    const k = this.clientKey(periodKey, denomination);
    const cur = new Set(this.expandedClients());
    if (cur.has(k)) cur.delete(k);
    else cur.add(k);
    this.expandedClients.set(cur);
  }

  isClientExpanded(periodKey: string, denomination: string): boolean {
    return this.expandedClients().has(this.clientKey(periodKey, denomination));
  }

  rows = computed<PeriodRow[]>(() => {
    const period = this.scale();
    const raw = this.data.rawData ?? [];

    const byKey = new Map<
      string,
      {
        anchor: Date;
        ca: number;
        clientEntries: Map<string, ClientEntry[]>;
      }
    >();
    let grandTotal = 0;

    for (const d of raw) {
      const dt = new Date(d.date);
      if (isNaN(dt.getTime())) continue;
      const key = WidgetHelperService.getDateForPeriod(d.date, period);
      if (!key) continue;
      let bucket = byKey.get(key);
      if (!bucket) {
        bucket = { anchor: dt, ca: 0, clientEntries: new Map() };
        byKey.set(key, bucket);
      }
      if (dt < bucket.anchor) bucket.anchor = dt;
      bucket.ca += d.montant ?? 0;
      const client = d.tiersDenomination || "Inconnu";
      const entry: ClientEntry = {
        date: d.date,
        dateLabel: dt.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        montant: d.montant ?? 0,
        factureIds: d.factureIds ?? [],
        commandeIds: d.commandeIds ?? [],
      };
      const arr = bucket.clientEntries.get(client);
      if (arr) arr.push(entry);
      else bucket.clientEntries.set(client, [entry]);
      grandTotal += d.montant ?? 0;
    }

    const keys = [...byKey.keys()].sort((a, b) => {
      return byKey.get(a)!.anchor.getTime() - byKey.get(b)!.anchor.getTime();
    });

    type ChronoRow = Omit<PeriodRow, "variationPct" | "partPct" | "color">;
    const chrono: ChronoRow[] = [];
    let runningCumul = 0;
    for (const key of keys) {
      const b = byKey.get(key)!;
      runningCumul += b.ca;
      const clients: ClientRow[] = [...b.clientEntries.entries()]
        .map(([denom, entries]) => {
          const ca = entries.reduce((s, e) => s + e.montant, 0);
          return {
            denomination: denom,
            ca,
            partPeriod: b.ca > 0 ? (ca / b.ca) * 100 : 0,
            nbEntries: entries.length,
            entries: entries.sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
            ),
          };
        })
        .sort((a, b) => b.ca - a.ca);
      chrono.push({
        key,
        label: this.formatPeriodLabel(key, b.anchor, period),
        anchor: b.anchor,
        ca: b.ca,
        cumul: runningCumul,
        nbClients: clients.length,
        clients,
      });
    }

    const enriched: PeriodRow[] = chrono.map((r, i) => {
      const prev = i > 0 ? chrono[i - 1] : null;
      const variationPct =
        prev && prev.ca > 0 ? ((r.ca - prev.ca) / prev.ca) * 100 : null;
      return {
        ...r,
        variationPct,
        partPct: grandTotal > 0 ? (r.ca / grandTotal) * 100 : 0,
        color: "",
      };
    });

    const ranked = [...enriched].sort((a, b) => b.ca - a.ca);
    ranked.forEach((r, i) => {
      r.color = ROW_PALETTE[i % ROW_PALETTE.length];
    });

    return this.sortRows(enriched);
  });

  private sortRows(rows: PeriodRow[]): PeriodRow[] {
    const key = this.sortKey();
    const desc = this.sortDesc();
    const mult = desc ? -1 : 1;
    return [...rows].sort((a, b) => {
      if (key === "date") return (a.anchor.getTime() - b.anchor.getTime()) * mult;
      if (key === "ca") return (a.ca - b.ca) * mult;
      return (a.cumul - b.cumul) * mult;
    });
  }

  /** Stats cards : Total cumulé final, Moyenne/période, Meilleure période,
   *  Croissance globale depuis le début. */
  stats = computed(() => {
    const rs = this.rows();
    if (!rs.length) {
      return {
        total: 0, totalFormatted: "0 €",
        avg: 0, avgFormatted: "—",
        bestLabel: "—", bestCA: 0, bestCAFormatted: "—",
        growthLabel: "—", growthPct: 0,
        count: 0,
      };
    }
    // Pour la stats "Meilleur mois", on classe par CA période (pas cumul)
    const sortedByCa = [...rs].sort((a, b) => b.ca - a.ca);
    const best = sortedByCa[0];
    const total = rs.reduce((s, r) => s + r.ca, 0);

    // Croissance : dernier CA période vs premier CA période (chronologique)
    const chrono = [...rs].sort((a, b) => a.anchor.getTime() - b.anchor.getTime());
    const firstCa = chrono[0].ca;
    const lastCa = chrono[chrono.length - 1].ca;
    const growthPct = firstCa > 0 ? ((lastCa - firstCa) / firstCa) * 100 : 0;
    const growthLabel =
      rs.length >= 2
        ? `${growthPct > 0 ? "+" : ""}${growthPct.toFixed(0)}%`
        : "—";

    return {
      total,
      totalFormatted: this.formatEuro(total),
      avg: total / rs.length,
      avgFormatted: this.formatEuro(total / rs.length),
      bestLabel: best.label,
      bestCA: best.ca,
      bestCAFormatted: this.formatEuro(best.ca),
      growthLabel,
      growthPct,
      count: rs.length,
    };
  });

  private formatPeriodLabel(
    key: string,
    anchor: Date,
    period: CaCumuleScale,
  ): string {
    switch (period) {
      case "j":
        return anchor.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      case "s":
        return key;
      case "m":
        return anchor.toLocaleDateString("fr-FR", {
          month: "long",
          year: "numeric",
        });
      case "a":
        return anchor.toLocaleDateString("fr-FR", { year: "numeric" });
    }
  }

  formatEuro(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return WidgetHelperService.formatNumeric(n) + " €";
  }

  formatEuroExact(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  /** Classe CSS sémantique pour une variation (positive verte, négative rouge). */
  variationClass(pct: number | null): string {
    if (pct == null) return "on-background-alt";
    if (pct > 0.5) return "on-valid";
    if (pct < -0.5) return "on-error";
    return "on-background-alt";
  }

  async exportExcel(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportCaCumuleExcel({
        rows: this.rows(),
        stats: this.stats(),
        scaleLabel: this.scaleLabel[this.scale()],
        scaleLabelSingularLower: this.scaleLabelSingularLower[this.scale()],
        timeScaleLabel: this.data.timeScaleLabel,
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
